import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';

export default function FinalCTASection() {
  const { t, isRTL } = useI18n();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-[#5B3FD0] overflow-hidden">
      
      {/* Decorative Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 2px, transparent 0)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Floating Shapes */}
      <div className="absolute top-20 left-[10%] w-32 h-32 bg-white/10 rounded-full blur-2xl animate-float" style={{ animationDuration: '8s' }}></div>
      <div className="absolute top-40 right-[15%] w-24 h-24 bg-[#9FFFCB]/20 rounded-full blur-2xl animate-float" style={{ animationDuration: '7s', animationDelay: '1s' }}></div>
      <div className="absolute bottom-32 left-[20%] w-40 h-40 bg-white/10 rounded-full blur-2xl animate-float" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>
      <div className="absolute bottom-20 right-[25%] w-20 h-20 bg-[#9FFFCB]/20 rounded-full blur-2xl animate-float" style={{ animationDuration: '6s', animationDelay: '0.5s' }}></div>

      {/* Geometric Shapes */}
      <div className="absolute top-16 right-[8%] w-16 h-16 border-4 border-white/20 rounded-2xl rotate-12 animate-float" style={{ animationDuration: '10s' }}></div>
      <div className="absolute bottom-24 left-[12%] w-12 h-12 border-4 border-[#9FFFCB]/30 rounded-full animate-float" style={{ animationDuration: '8s', animationDelay: '1.5s' }}></div>

      <div className="container mx-auto px-4 sm:px-8 md:px-12 lg:px-16 relative z-10">
        
        <div className="max-w-5xl mx-auto">
          
          {/* Main Content Card */}
          <div className="relative bg-white/95 backdrop-blur-xl rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-12 lg:p-16 shadow-2xl">
            
            {/* Decorative Elements */}
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#9FFFCB] opacity-20 rounded-3xl rotate-12 animate-float" style={{ animationDuration: '7s' }}></div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#B18AE0] opacity-20 rounded-2xl rotate-45 animate-float" style={{ animationDuration: '8s', animationDelay: '1s' }}></div>

            {/* Content */}
            <div className="relative text-center space-y-8 md:space-y-10">
              
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-[#F8F5FF] to-[#F0FDFA] border-2 border-[#9FFFCB]/30 rounded-full shadow-lg">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 bg-[#5B3FD0] rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-[#B18AE0] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-[#9FFFCB] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
                <span className="text-sm font-bold text-[#5B3FD0] uppercase tracking-wider">{t.finalCTA.badge}</span>
              </div>

              {/* Title */}
              <div className="space-y-4">
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C2C2C] leading-tight">
                  {t.finalCTA.title}
                </h2>
                <p className="text-base md:text-lg lg:text-xl text-[#707070] leading-relaxed max-w-3xl mx-auto">
                  {t.finalCTA.intro}
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Button 
                  variant="primary" 
                  className="text-base md:text-lg px-8 md:px-10 py-4 md:py-5 shadow-2xl hover:shadow-[#5B3FD0]/30 hover:scale-105 transition-all"
                  onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
                >
                  {t.finalCTA.primaryCTA}
                  <svg className={`w-5 h-5 md:w-6 md:h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Button>
                
                <button 
                  onClick={() => {
                    alert('Coming Soon! This feature is currently under development.');
                  }}
                  className="group flex items-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-white border-3 border-[#5B3FD0] text-[#5B3FD0] rounded-xl md:rounded-2xl font-bold text-base md:text-lg shadow-lg hover:bg-[#5B3FD0] hover:text-white transition-all"
                >
                  <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#5B3FD0]/10 group-hover:bg-white/20 transition-all">
                    <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span>{t.finalCTA.secondaryCTA}</span>
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 md:gap-8 pt-6 md:pt-8 border-t-2 border-[#E4E4E7]">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#9FFFCB] rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2C2C2C]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <span className="text-sm md:text-base text-[#2C2C2C] font-medium">{t.finalCTA.features.freeConsultation}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#9FFFCB] rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2C2C2C]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <span className="text-sm md:text-base text-[#2C2C2C] font-medium">{t.finalCTA.features.noHiddenFees}</span>
                </div>

                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#9FFFCB] rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2C2C2C]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <span className="text-sm md:text-base text-[#2C2C2C] font-medium">{t.finalCTA.features.expertSupport}</span>
                </div>
              </div>

              {/* Closing Line */}
              <div className="pt-4">
                <p className="text-lg md:text-2xl font-bold text-[#5B3FD0]">
                  {t.finalCTA.closing}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
